/**
 * Name Similarity and Passenger Identity Consistency Utility
 * Used for loyalty ticket validation to ensure all tickets in a compensation basket
 * belong to the same person (or almost the same name).
 */

export interface NameConsistencyResult {
  isConsistent: boolean;
  score: number; // 0 to 100%
  status: 'exact' | 'variant' | 'conflict';
  primaryName: string;
  primaryCni: string;
  primaryPhone: string;
  distinctNames: string[];
  conflictingTickets: any[];
  message: string;
}

/**
 * Normalizes a passenger name by stripping accents, titles, punctuation, and extra spaces.
 */
export function normalizePassengerName(name: string | null | undefined): string {
  if (!name) return '';
  let str = name.trim().toUpperCase();
  // Strip accents / diacritics
  try {
    str = str.normalize('NFD').replace(/[\u0300-\u036f]/g, '');
  } catch (e) {
    // Fallback if normalize not supported
  }
  // Strip titles / civilities
  str = str.replace(/\b(MR|MME|MLLE|MONSIEUR|MADAME|MADEMOISELLE|DR|DOCTEUR|PR|PROFESSEUR)\b/g, ' ');
  // Strip non-alphanumeric
  str = str.replace(/[^A-Z0-9\s]/g, ' ');
  // Collapse whitespace
  return str.replace(/\s+/g, ' ').trim();
}

/**
 * Calculates standard Levenshtein distance between two strings.
 */
export function levenshteinDistance(s1: string, s2: string): number {
  if (!s1) return s2 ? s2.length : 0;
  if (!s2) return s1.length;
  if (s1 === s2) return 0;

  const len1 = s1.length;
  const len2 = s2.length;
  const dp: number[][] = [];

  for (let i = 0; i <= len1; i++) {
    dp[i] = [i];
  }
  for (let j = 0; j <= len2; j++) {
    dp[0][j] = j;
  }

  for (let i = 1; i <= len1; i++) {
    for (let j = 1; j <= len2; j++) {
      const cost = s1[i - 1] === s2[j - 1] ? 0 : 1;
      dp[i][j] = Math.min(
        dp[i - 1][j] + 1,
        dp[i][j - 1] + 1,
        dp[i - 1][j - 1] + cost
      );
    }
  }

  return dp[len1][len2];
}

/**
 * Calculates composite name similarity score (0 to 100).
 * Handles word re-ordering ("KAMGA JEAN" vs "JEAN KAMGA") and 
 * partial inclusion ("KAMGA JEAN" vs "KAMGA JEAN PIERRE").
 */
export function calculateNameSimilarity(name1: string, name2: string): number {
  const norm1 = normalizePassengerName(name1);
  const norm2 = normalizePassengerName(name2);

  if (!norm1 && !norm2) return 100;
  if (!norm1 || !norm2) return 0;
  if (norm1 === norm2) return 100;

  const tokens1 = norm1.split(' ').filter(t => t.length > 0);
  const tokens2 = norm2.split(' ').filter(t => t.length > 0);

  const set1 = new Set(tokens1);
  const set2 = new Set(tokens2);

  const intersection = new Set([...set1].filter(x => set2.has(x)));
  const union = new Set([...set1, ...set2]);

  // If all words of the shorter name are present in the longer name (e.g. "KAMGA JEAN" in "KAMGA JEAN PIERRE")
  const minTokens = Math.min(set1.size, set2.size);
  if (minTokens >= 1 && intersection.size >= minTokens) {
    const tokenScore = Math.round((intersection.size / Math.max(set1.size, set2.size)) * 100);
    // If exact subset, guarantee high score (at least 88%)
    return Math.max(88, tokenScore);
  }

  // Jaccard word similarity
  const jaccardScore = union.size > 0 ? (intersection.size / union.size) * 100 : 0;

  // Character-level Levenshtein similarity
  const maxLen = Math.max(norm1.length, norm2.length);
  const dist = levenshteinDistance(norm1, norm2);
  const levScore = maxLen > 0 ? Math.round((1.0 - dist / maxLen) * 100) : 0;

  return Math.round(Math.max(jaccardScore, levScore));
}

/**
 * Determines whether two passenger profiles belong to the same person or almost the same name.
 */
export function isPassengerCompatible(
  p1: { nompassager?: string; cni?: string; contact?: string },
  p2: { nompassager?: string; cni?: string; contact?: string }
): { compatible: boolean; score: number; reason: string } {
  if (!p1 || !p2) return { compatible: false, score: 0, reason: 'Profil passager manquant' };

  // 1. CNI Match: If non-empty and identical -> definitive match
  const cni1 = (p1.cni || '').trim().toUpperCase();
  const cni2 = (p2.cni || '').trim().toUpperCase();
  if (cni1 && cni2 && cni1 === cni2) {
    return { compatible: true, score: 100, reason: 'CNI identique' };
  }

  // 2. Phone Match
  const phone1 = (p1.contact || '').replace(/[^0-9]/g, '');
  const phone2 = (p2.contact || '').replace(/[^0-9]/g, '');
  const samePhone = phone1.length >= 8 && phone1 === phone2;

  // 3. Name Similarity
  const score = calculateNameSimilarity(p1.nompassager || '', p2.nompassager || '');

  if (score === 100) {
    return { compatible: true, score: 100, reason: 'Nom 100% identique' };
  }

  if (score >= 85) {
    return { compatible: true, score, reason: 'Variante orthographique très proche' };
  }

  if (score >= 75) {
    return { compatible: true, score, reason: 'Variante acceptable (nom composé ou inversion)' };
  }

  // Corroborated with phone number
  if (samePhone && score >= 50) {
    return { compatible: true, score: Math.max(score, 80), reason: 'Téléphone identique avec nom proche' };
  }

  return { compatible: false, score, reason: 'Noms divergents sans identifiant commun' };
}

/**
 * Checks all tickets in a basket against each other to evaluate overall passenger identity consistency.
 */
export function checkPassengerConsistency(tickets: any[], fallbackBeneficiaryName?: string): NameConsistencyResult {
  if (!tickets || tickets.length === 0) {
    return {
      isConsistent: true,
      score: 100,
      status: 'exact',
      primaryName: fallbackBeneficiaryName || '',
      primaryCni: '',
      primaryPhone: '',
      distinctNames: fallbackBeneficiaryName ? [fallbackBeneficiaryName] : [],
      conflictingTickets: [],
      message: 'Panier vide'
    };
  }

  // Extract primary passenger from first ticket
  const firstTicket = tickets[0];
  const primaryPassager = firstTicket.passager || {};
  const primaryName = (primaryPassager.nompassager || fallbackBeneficiaryName || '').trim();
  const primaryCni = (primaryPassager.cni || '').trim();
  const primaryPhone = (primaryPassager.contact || '').trim();

  if (tickets.length === 1) {
    return {
      isConsistent: true,
      score: 100,
      status: 'exact',
      primaryName,
      primaryCni,
      primaryPhone,
      distinctNames: [primaryName],
      conflictingTickets: [],
      message: 'Un seul billet dans le panier'
    };
  }

  const distinctNamesSet = new Set<string>();
  if (primaryName) distinctNamesSet.add(primaryName);

  let lowestScore = 100;
  const conflictingTickets: any[] = [];
  let allExact = true;

  for (let i = 1; i < tickets.length; i++) {
    const t = tickets[i];
    const currPassager = t.passager || {};
    const currName = (currPassager.nompassager || '').trim();
    if (currName) distinctNamesSet.add(currName);

    const comp = isPassengerCompatible(primaryPassager, currPassager);

    if (comp.score < lowestScore) {
      lowestScore = comp.score;
    }

    if (comp.score < 100) {
      allExact = false;
    }

    if (!comp.compatible) {
      conflictingTickets.push(t);
    }
  }

  const distinctNames = Array.from(distinctNamesSet);

  if (conflictingTickets.length > 0) {
    return {
      isConsistent: false,
      score: lowestScore,
      status: 'conflict',
      primaryName,
      primaryCni,
      primaryPhone,
      distinctNames,
      conflictingTickets,
      message: `Incohérence détectée : ${conflictingTickets.length} billet(s) appartiennent à des passagers différents (${distinctNames.join(' vs ')}).`
    };
  }

  if (!allExact) {
    return {
      isConsistent: true,
      score: lowestScore,
      status: 'variant',
      primaryName,
      primaryCni,
      primaryPhone,
      distinctNames,
      conflictingTickets: [],
      message: `Variantes acceptées (${lowestScore}% similarité) : ${distinctNames.join(' ~ ')}.`
    };
  }

  return {
    isConsistent: true,
    score: 100,
    status: 'exact',
    primaryName,
    primaryCni,
    primaryPhone,
    distinctNames,
    conflictingTickets: [],
    message: 'Tous les billets appartiennent au même voyageur.'
  };
}
